import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const AdSidebar = ({ position = 'left', currentAdId, setCurrentAdId, otherSidebarAdId }) => {
    const [banners, setBanners] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const otherAdIdRef = useRef(otherSidebarAdId);
    const location = useLocation();

    // Exclude paths
    const isSuperAdmin = location.pathname.startsWith('/superadmin');
    const isLandingPage = location.pathname === '/';
    const isAuthPage = ['/login', '/register'].includes(location.pathname);

    // Update ref when prop changes
    useEffect(() => {
        otherAdIdRef.current = otherSidebarAdId;
    }, [otherSidebarAdId]);

    // Find next valid index that doesn't match other sidebar
    const getNextValidIndex = useCallback((currentIdx, adsArray) => {
        if (adsArray.length <= 1) return currentIdx;

        // If we only have 2 ads, simply rotate to allow resolving deadlocks via alternating updates
        if (adsArray.length === 2) {
            return (currentIdx + 1) % adsArray.length;
        }

        let nextIdx = (currentIdx + 1) % adsArray.length;
        let attempts = 0;
        const otherAdId = otherAdIdRef.current;

        // Keep looking for an ad that's different from the other sidebar
        while (adsArray[nextIdx]?.id === otherAdId && attempts < adsArray.length) {
            nextIdx = (nextIdx + 1) % adsArray.length;
            attempts++;
        }

        return nextIdx;
    }, []);

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const q = query(
                    collection(db, 'ad_banners'),
                    where('status', '==', 'active')
                );
                const querySnapshot = await getDocs(q);
                const fetchedBanners = [];
                querySnapshot.forEach((doc) => {
                    fetchedBanners.push({ id: doc.id, ...doc.data() });
                });

                // Shuffle ads for randomness
                const shuffled = fetchedBanners.sort(() => 0.5 - Math.random());
                setBanners(shuffled);

                // Set different starting index for left vs right
                if (shuffled.length > 1) {
                    const startIndex = position === 'right' ? 1 : 0;
                    setCurrentIndex(startIndex);
                }
            } catch (error) {
                console.error("Error fetching ads:", error);
            }
        };

        if (!isSuperAdmin && !isLandingPage && !isAuthPage) {
            fetchBanners();
        }
    }, [isSuperAdmin, isLandingPage, isAuthPage, position]);

    // Check if current ad conflicts with other sidebar and skip if needed
    // This effect handles the case where the *other* sidebar changed and created a conflict
    useEffect(() => {
        if (banners.length > 2 && banners[currentIndex]) {
            const currentAd = banners[currentIndex];
            // Only force skip if we have enough ads (>2) to find a non-conflicting one.
            // If <= 2, we tolerate temporary duplication to allow rotation.
            if (currentAd.id === otherSidebarAdId) {
                const nextIdx = getNextValidIndex(currentIndex, banners);
                if (nextIdx !== currentIndex) {
                    setCurrentIndex(nextIdx);
                }
            }
        }
    }, [otherSidebarAdId, banners, currentIndex, getNextValidIndex]);

    // Update parent component when current ad changes
    useEffect(() => {
        if (banners.length > 0 && banners[currentIndex]) {
            setCurrentAdId(banners[currentIndex].id);
        }
    }, [currentIndex, banners, setCurrentAdId]);

    // Rotate ads - different timing for left vs right to avoid synchronization
    useEffect(() => {
        if (banners.length > 1) {
            // Left sidebar: 10 seconds, Right sidebar: 14 seconds
            // Using different intervals prevents them from changing at the exact same time
            const intervalTime = position === 'left' ? 10000 : 14000;

            const interval = setInterval(() => {
                setCurrentIndex((prev) => {
                    return getNextValidIndex(prev, banners);
                });
            }, intervalTime);
            return () => clearInterval(interval);
        }
    }, [banners, getNextValidIndex, position]);

    if (isSuperAdmin || isLandingPage || isAuthPage || banners.length === 0) return null;

    const currentBanner = banners[currentIndex];
    if (!currentBanner) return null;

    const secondBanner = banners.length > 1 ? banners[(currentIndex + 1) % banners.length] : null;

    const renderBanner = (banner) => {
        if (!banner) return null;
        return (
            <div
                className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 animate-slide-up"
                key={banner.id}
            >
                {banner.link ? (
                    <a href={banner.link} target="_blank" rel="noopener noreferrer">
                        <img
                            src={banner.imageUrl}
                            alt="Advertisement"
                            className="w-full h-auto object-cover cursor-pointer hover:opacity-95 transition-opacity"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </a>
                ) : (
                    <img
                        src={banner.imageUrl}
                        alt="Advertisement"
                        className="w-full h-auto object-cover cursor-pointer hover:opacity-95 transition-opacity"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <aside className="hidden xl:block w-48 flex-shrink-0">
            <style>
                {`
                @keyframes slideUpFade {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-up {
                    animation: slideUpFade 0.8s ease-out;
                }
                `}
            </style>
            <div className="sticky top-20 space-y-4">
                {renderBanner(currentBanner)}
                {/* Show second banner if available and different from first */}
                {secondBanner && secondBanner.id !== currentBanner.id && renderBanner(secondBanner)}
            </div>
        </aside>
    );
};

export default AdSidebar;
